import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import moment from "moment";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import _ from "lodash";
import centreServices from "app/services/centre-services";
import warehouseBinServices from "app/services/warehouse-bin-services";
import { downloadBuffer } from "app/utils/appHelpers";
import BoardPlan, { groupBinsIntoRacks } from "./BoardPlan";
import RackDrawer from "./RackDrawer";
import GenerateAisleModal from "./GenerateAisleModal";
import WarehouseImportModal from "./WarehouseImportModal";
import BinLabelsDialog from "./BinLabelsDialog";
import WarehouseHelpDialog from "./WarehouseHelpDialog";
import UnplacedTray from "./UnplacedTray";
import RackPalette from "./RackPalette";
import RackTypesModal from "./RackTypesModal";
import ZonesModal from "./ZonesModal";
import rackTemplateServices from "app/services/rack-template-services";
import ExcelExportButton from "app/widgets/Shared/ExcelExportButton";

const COLOR_MODES = [
    { value: "ocupacion", label: "Ocupación", help: "Más intenso = más posiciones ocupadas en ese rack" },
    { value: "vacias", label: "Vacías", help: "Resalta los racks sin ningún SKU asignado" },
    { value: "recorrido", label: "Recorrido", help: "Del inicio del recorrido de picking (claro) al final (intenso)" },
    { value: "erp", label: "Del ERP", help: "Resalta los racks cuyas posiciones llegaron desde el ERP" },
    { value: "zonas", label: "Zonas", help: "Pinta cada rack con el color de su zona" },
];

// Perfiles que pueden rediseñar la bodega. El resto ve el tablero pero no lo
// edita: ver dónde está un producto es distinto de mover un rack.
const EDIT_PROFILES = ["superadmin", "customer_admin", "supervisor", "warehouse"];

const WarehouseBoard = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();

    const [centres, setCentres] = useState([]);
    const [centreId, setCentreId] = useState("");
    const [layout, setLayout] = useState(null);
    const [loading, setLoading] = useState(false);
    const [colorMode, setColorMode] = useState("ocupacion");

    const [selectedRack, setSelectedRack] = useState(null);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [labelsOpen, setLabelsOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    // Modo editor: muestra la paleta de piezas y deja estampar racks en el plano.
    const [editorOpen, setEditorOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [placingRotation, setPlacingRotation] = useState(0);
    const [placingZoneId, setPlacingZoneId] = useState(null);
    const [typesOpen, setTypesOpen] = useState(false);
    const [zonesOpen, setZonesOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [resequencing, setResequencing] = useState(false);

    const canEdit = useMemo(
        () => _.some(authUser?.profiles, (profile) => EDIT_PROFILES.includes(profile.name)),
        [authUser]
    );

    useEffect(() => {
        const loadCentres = async () => {
            try {
                const data = await centreServices.searchByCompany();
                const list = Array.isArray(data) ? data : data?.data || [];
                setCentres(list);
                if (list.length) setCentreId(list[0].id);
            } catch (_error) {
                setCentres([]);
            }
        };
        loadCentres();
    }, []);

    const loadLayout = useCallback(async () => {
        if (!centreId) return;
        try {
            setLoading(true);
            const data = await warehouseBinServices.getLayout(centreId);
            setLayout(data);
        } catch (_error) {
            setLayout(null);
        } finally {
            setLoading(false);
        }
    }, [centreId]);

    const loadTemplates = useCallback(async () => {
        try {
            const data = await rackTemplateServices.getTemplates();
            setTemplates(data || []);
        } catch (_error) {
            setTemplates([]);
        }
    }, []);

    useEffect(() => {
        if (editorOpen) loadTemplates();
    }, [editorOpen, loadTemplates]);

    // Las zonas viajan con el plano, así que no hace falta otra llamada.
    const zones = useMemo(() => layout?.zones || [], [layout]);

    // Estampa el rack elegido en la celda. Se recarga el plano completo porque el
    // recorrido se recalcula en el servidor y afecta a toda la bodega.
    const placeRack = async (x, y) => {
        if (!selectedTemplate) return;

        try {
            const result = await warehouseBinServices.placeRack({
                centre_id: centreId,
                rack_template_id: selectedTemplate.id,
                warehouse_zone_id: placingZoneId || undefined,
                layout_x: x,
                layout_y: y,
                rotation: placingRotation,
            });
            enqueueSnackbar(
                `${result.created_count} posiciones · pasillo ${result.aisle} rack ${result.rack}`,
                { variant: "success" }
            );
            loadLayout();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo poner el rack", { variant: "error" });
        }
    };

    useEffect(() => {
        loadLayout();
    }, [loadLayout]);

    // Memoizado y no `layout?.bins || []` a secas: ese fallback crea un array
    // nuevo en cada render y hace que los useMemo de abajo recalculen siempre,
    // reagrupando la bodega entera sin que haya cambiado nada.
    const bins = useMemo(() => layout?.bins || [], [layout]);
    const summary = layout?.summary;

    const unplacedBins = useMemo(() => bins.filter((bin) => !bin.placed), [bins]);

    // El drawer necesita el rack recalculado tras cada recarga, no la copia que
    // quedó capturada al abrirlo: si no, asignar un SKU no refresca el conteo.
    const liveRack = useMemo(() => {
        if (!selectedRack) return null;
        const racks = groupBinsIntoRacks(bins);
        return racks.find((rack) => rack.key === selectedRack.key) || selectedRack;
    }, [selectedRack, bins]);

    const moveRack = async (rack, x, y) => {
        try {
            // Todas las posiciones del rack se mueven juntas: comparten celda.
            await warehouseBinServices.bulkLayout(
                centreId,
                rack.bins.map((bin) => ({ id: bin.id, layout_x: x, layout_y: y }))
            );
            loadLayout();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo mover el rack", { variant: "error" });
        }
    };

    const resequence = async () => {
        try {
            setResequencing(true);
            const result = await warehouseBinServices.resequence(centreId);
            enqueueSnackbar(result.message || "Recorrido recalculado", { variant: "success" });
            loadLayout();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo recalcular", { variant: "error" });
        } finally {
            setResequencing(false);
        }
    };

    const exportBins = async () => {
        try {
            setExporting(true);
            const data = await warehouseBinServices.exportBins(`?centre_id=${centreId}`);
            downloadBuffer(data, `posiciones_${moment().format("YYYYMMDDHHmmss")}.xlsx`);
        } catch (_error) {
            // El interceptor muestra el mensaje genérico.
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box>
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <Typography variant="h1" sx={{ mb: 0 }}>
                            Posiciones de bodega
                        </Typography>
                        <Tooltip title="Cómo armar la bodega">
                            <IconButton size="small" onClick={() => setHelpOpen(true)}>
                                <HelpOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        Dónde vive cada producto y en qué orden recorrer la bodega para preparar.
                    </Typography>
                </Box>

                <TextField
                    select
                    size="small"
                    label="Bodega"
                    value={centreId}
                    onChange={(event) => setCentreId(event.target.value)}
                    sx={{ minWidth: 240 }}
                >
                    {centres.map((centre) => (
                        <MenuItem key={centre.id} value={centre.id}>
                            {centre.name}
                        </MenuItem>
                    ))}
                    {!centres.length && <MenuItem disabled value="">Sin bodegas</MenuItem>}
                </TextField>
            </Stack>

            {summary && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    <Chip label={`${summary.total} posiciones`} />
                    <Chip label={`${summary.placed} en el plano`} color="primary" variant="outlined" />
                    {summary.empty > 0 && (
                        <Chip label={`${summary.empty} vacías`} color="warning" variant="outlined" />
                    )}
                    {summary.from_erp > 0 && (
                        <Chip label={`${summary.from_erp} del ERP`} color="info" variant="outlined" />
                    )}
                    {summary.unstructured > 0 && (
                        <Tooltip title="Llegaron del ERP como texto libre y no se pudieron estructurar. No entran en el recorrido hasta que se ubiquen.">
                            <Chip label={`${summary.unstructured} sin estructura`} color="error" variant="outlined" />
                        </Tooltip>
                    )}
                </Stack>
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Stack
                        direction={{ xs: "column", lg: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", lg: "center" }}
                        spacing={2}
                    >
                        <Box>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={colorMode}
                                onChange={(_event, value) => value && setColorMode(value)}
                            >
                                {COLOR_MODES.map((mode) => (
                                    <ToggleButton key={mode.value} value={mode.value}>
                                        {mode.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {COLOR_MODES.find((mode) => mode.value === colorMode)?.help}
                            </Typography>
                        </Box>

                        {canEdit && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    size="small"
                                    variant={editorOpen ? "contained" : "outlined"}
                                    color={editorOpen ? "secondary" : "primary"}
                                    startIcon={<DashboardCustomizeOutlinedIcon />}
                                    disabled={!centreId}
                                    onClick={() => {
                                        setEditorOpen((prev) => !prev);
                                        setSelectedTemplate(null);
                                    }}
                                >
                                    {editorOpen ? "Cerrar editor" : "Editor"}
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddBoxOutlinedIcon />}
                                    disabled={!centreId}
                                    onClick={() => setGenerateOpen(true)}
                                >
                                    Generar pasillo
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<UploadFileOutlinedIcon />}
                                    disabled={!centreId}
                                    onClick={() => setImportOpen(true)}
                                >
                                    Importar
                                </Button>
                                <ExcelExportButton
                                    size="small"
                                    loading={exporting}
                                    disabled={!centreId}
                                    onClick={exportBins}
                                />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PrintOutlinedIcon />}
                                    disabled={!centreId}
                                    onClick={() => setLabelsOpen(true)}
                                >
                                    Etiquetas
                                </Button>
                                <LoadingButton
                                    size="small"
                                    variant="outlined"
                                    loading={resequencing}
                                    startIcon={<RouteOutlinedIcon />}
                                    disabled={!centreId}
                                    onClick={resequence}
                                >
                                    Recalcular recorrido
                                </LoadingButton>
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {loading && (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress />
                </Stack>
            )}

            {!loading && (
                <Paper sx={{ p: 3 }}>
                    {canEdit && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                            Cada celda es un rack. Haz clic para ver sus niveles y asignar SKUs; arrástralo para
                            moverlo en el plano.
                        </Typography>
                    )}

                    <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">
                        {canEdit && editorOpen && (
                            <RackPalette
                                templates={templates}
                                zones={zones}
                                selectedTemplate={selectedTemplate}
                                onSelectTemplate={setSelectedTemplate}
                                rotation={placingRotation}
                                onRotate={() => setPlacingRotation((prev) => (prev + 90) % 360)}
                                zoneId={placingZoneId}
                                onZoneChange={setPlacingZoneId}
                                onManageTemplates={() => setTypesOpen(true)}
                                onManageZones={() => setZonesOpen(true)}
                            />
                        )}

                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <BoardPlan
                                bins={bins}
                                bounds={layout?.bounds}
                                colorMode={colorMode}
                                editable={canEdit}
                                onSelectRack={setSelectedRack}
                                onMoveRack={moveRack}
                                zones={zones}
                                placingTemplate={canEdit && editorOpen ? selectedTemplate : null}
                                placingRotation={placingRotation}
                                onPlaceAt={placeRack}
                            />
                        </Box>
                    </Stack>

                    {unplacedBins.length > 0 && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <UnplacedTray
                                bins={unplacedBins}
                                canEdit={canEdit}
                                centreId={centreId}
                                onChanged={loadLayout}
                            />
                        </>
                    )}
                </Paper>
            )}

            <RackDrawer
                rack={liveRack}
                open={Boolean(selectedRack)}
                canEdit={canEdit}
                centreId={centreId}
                onClose={() => setSelectedRack(null)}
                onChanged={loadLayout}
            />

            <GenerateAisleModal
                open={generateOpen}
                centreId={centreId}
                onClose={() => setGenerateOpen(false)}
                onGenerated={loadLayout}
            />

            <WarehouseImportModal
                open={importOpen}
                centreId={centreId}
                onClose={() => setImportOpen(false)}
                onImported={loadLayout}
            />

            <BinLabelsDialog
                open={labelsOpen}
                centreId={centreId}
                onClose={() => setLabelsOpen(false)}
            />

            <WarehouseHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

            <RackTypesModal
                open={typesOpen}
                onClose={() => setTypesOpen(false)}
                onChanged={loadTemplates}
            />

            <ZonesModal
                open={zonesOpen}
                centreId={centreId}
                onClose={() => setZonesOpen(false)}
                onChanged={loadLayout}
            />
        </Box>
    );
};

export default WarehouseBoard;
